import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import UIElement from '@ckeditor/ckeditor5-engine/src/view/uielement';
import {
  toWidget,
  viewToModelPositionOutsideModelElement,
} from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import { INSERT_EMOJI_COMMAND } from './constants';
import { InsertEmojiCommand } from './insert-emoji-command';
import { EmojiPluginConfiguration } from './types';
import './emoji.css';

export class Emoji extends Plugin {
  static get requires() {
    return [Widget];
  }

  static get pluginName(): string {
    return 'Emoji';
  }

  init() {
    this.defineSchema();
    this.defineConverters();
    this.editor.commands.add(
      INSERT_EMOJI_COMMAND,
      new InsertEmojiCommand(this.editor),
    );
    this.editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(this.editor.model, (viewElement) =>
        viewElement.hasClass('emoji'),
      ),
    );
    this.editor.config.define<EmojiPluginConfiguration>('emoji', {
      renderer: (emoji, container) => {
        // eslint-disable-next-line no-console
        console.log('render', emoji, 'to', container);
      },
    });
  }

  private defineSchema() {
    const { schema } = this.editor.model;

    schema.register('emoji', {
      allowWhere: '$text',
      isInline: true,
      isObject: true,
      allowAttributes: ['emoji'],
    });
  }

  private defineConverters() {
    const { conversion } = this.editor;
    const renderEmoji =
      this.editor.config.get<EmojiPluginConfiguration>('emoji').renderer;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: ['emoji'],
      },
      model: (viewElement, { writer: modelWriter }) => {
        /**
         * The viewElement here is supposed to be from the data view and not the
         * editing view. But there might be a bug in CKE5 where if the caret is
         * on the custom component that is rendered, the editing view is passed
         * instead, therefore the text becomes null. Here we use the fallback
         * attribute data-emoji.
         */
        const text = viewElement.getChild(0).data;
        const emoji =
          text == null
            ? viewElement.getAttribute('data-emoji')
            : text.slice(1, -1);

        return modelWriter.createElement('emoji', { emoji });
      },
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'emoji',
      view: (modelElement, { writer: viewWriter }) => {
        const emoji = modelElement.getAttribute('emoji');
        const reactWrapper = viewWriter.createUIElement(
          'span',
          {
            class: 'emoji__react-wrapper',
          },
          function (this: UIElement, domDocument: any) {
            const domElement = this.toDomElement(domDocument);

            renderEmoji(emoji, domElement);

            return domElement;
          },
        );

        const emojiView = viewWriter.createContainerElement(
          'span',
          {
            class: 'emoji',
            /**
             * Once we render a custom view, we lose access to the emoji in the
             * form of :emoji: in the innerText of this container element. So we
             * retain access to this to retrieve it in the upcast conversion.
             *
             * Upcast conversion should happen from data view and not editing view
             * but looks like there's a bug in CKE5 where the editing view is
             * passed upon editing (instead of the data view) when the caret is
             * on the custom element rendered into the view.
             */
            'data-emoji': emoji,
          },
          [reactWrapper],
        );

        return toWidget(emojiView, viewWriter);
      },
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'emoji',
      view: (modelItem, { writer: viewWriter }) => {
        const emoji = modelItem.getAttribute('emoji');

        const innerText = viewWriter.createText(`:${emoji}:`);
        const emojiView = viewWriter.createContainerElement(
          'span',
          {
            class: 'emoji',
          },
          [innerText],
        );

        return emojiView;
      },
    });
  }
}
